/**
 * Rich-text helpers for the gathering description (ENG-1870).
 *
 * Ported from gatherly-connect `src/lib/rich-text.ts` (ENG-899). Hosts author
 * descriptions in a rich-text editor on web and mobile, so new values contain
 * HTML while older values are plain text. Both shapes live in the same
 * `description` field, so every surface has to branch on which one it is
 * holding.
 *
 * Keep the tag probe, allowlists and URL rules identical to web: admin must
 * never render a tag or attribute that web would strip.
 */

// ---------------------------------------------------------------------------
// 1. isHtmlDescription — which of the two shapes is this?
// ---------------------------------------------------------------------------

/**
 * Matches a tag from the set the editor can emit (plus common pasted ones).
 * Deliberately NOT a generic `<[a-z]` probe: plain-text descriptions contain
 * things like `email me <me@joinfabrik.com>`, and misreading one of those as
 * HTML would silently drop text from the page.
 */
const HTML_TAG_PROBE =
  /<\/?(?:p|br|div|span|strong|b|em|i|u|s|strike|del|h[1-6]|ul|ol|li|blockquote|a|img|hr|code|pre|figure|figcaption|sub|sup|mark|small|table|thead|tbody|tr|td|th)(?:\s[^>]*)?\/?>/i;

export function isHtmlDescription(value: string | null | undefined): boolean {
  if (!value) return false;
  return HTML_TAG_PROBE.test(value);
}

// ---------------------------------------------------------------------------
// 2. sanitizeRichText — allowlist sanitizer for host-authored HTML
// ---------------------------------------------------------------------------

/**
 * Descriptions are host-authored and rendered to every attendee, so this IS an
 * XSS boundary — unlike `sanitizeBlastHtml`, which only massages trusted editor
 * output for email clients. Allowlist, not denylist: unknown tags and every
 * unlisted attribute (which covers all `on*` handlers and inline `style`) are
 * dropped.
 */
const ALLOWED_TAGS: ReadonlySet<string> = new Set([
  'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'del',
  'h2', 'h3', 'h4', 'ul', 'ol', 'li', 'blockquote', 'a', 'img', 'hr',
  'code', 'pre', 'span',
]);

const VOID_TAGS: ReadonlySet<string> = new Set(['br', 'img', 'hr']);

const ALLOWED_ATTRS: Record<string, ReadonlySet<string>> = {
  a: new Set(['href', 'title', 'target']),
  img: new Set(['src', 'alt', 'title', 'width', 'height']),
};

/** Tags whose *contents* are executable or parser-confusing, not just markup. */
const STRIP_WITH_CONTENT =
  /<(script|style|iframe|object|embed|noscript|template|svg|math)\b[\s\S]*?(?:<\/\1\s*>|$)/gi;

/** One tag, tolerating `>` inside quoted attribute values. */
const TAG = /<\/?([a-zA-Z][a-zA-Z0-9-]*)((?:"[^"]*"|'[^']*'|[^>"'])*)>/g;

const ATTR = /([a-zA-Z_:][-a-zA-Z0-9_:.]*)(?:\s*=\s*("[^"]*"|'[^']*'|[^\s"'=<>`]+))?/g;

const DATA_IMAGE = /^data:image\/(?:png|jpe?g|gif|webp);base64,[a-z0-9+/=]+$/i;

/**
 * Drop the characters a browser ignores inside an attribute value before it
 * parses a URL — control characters, spaces, line separators, BOM. Written as a
 * codepoint filter rather than a regex because the range includes control
 * characters, which are not allowed in a regex literal.
 */
function stripUrlNoise(value: string): string {
  let out = '';
  for (const char of value) {
    const code = char.codePointAt(0) ?? 0;
    const ignored =
      code <= 0x20 ||
      (code >= 0x7f && code <= 0xa0) ||
      code === 0x2028 ||
      code === 0x2029 ||
      code === 0xfeff;
    if (!ignored) out += char;
  }
  return out;
}

function escapeAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Returns the URL if it is safe to emit, else null. */
function safeUrl(raw: string, isImage: boolean): string | null {
  // The browser decodes entities and ignores embedded control characters before
  // it parses the scheme, so the check has to run on the decoded, stripped form
  // — `&#106;avascript:` and `java&#9;script:` both reach the URL parser as
  // `javascript:`.
  const probe = stripUrlNoise(decodeEntities(raw));
  if (!probe) return null;
  const value = raw.trim();
  if (/^(?:https?:|mailto:|tel:)/i.test(probe)) return value;
  if (isImage && DATA_IMAGE.test(probe)) return value;
  // Relative / same-document references.
  if (/^[/#?]/.test(probe)) return value;
  // Schemeless (e.g. `joinfabrik.com/x`) is fine; anything carrying a scheme we
  // did not allow above is not.
  if (!/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(probe)) return value;
  return null;
}

function sanitizeAttrs(tag: string, rawAttrs: string): string {
  const allowed = ALLOWED_ATTRS[tag];
  if (!allowed) return '';

  const out: string[] = [];
  let hasHref = false;
  for (const match of Array.from(rawAttrs.matchAll(ATTR))) {
    const name = match[1].toLowerCase();
    if (!allowed.has(name)) continue;
    const rawValue = match[2] ?? '';
    const value = /^["']/.test(rawValue) ? rawValue.slice(1, -1) : rawValue;
    if (name === 'href' || name === 'src') {
      const url = safeUrl(value, name === 'src');
      if (!url) continue;
      if (name === 'href') hasHref = true;
      out.push(`${name}="${escapeAttr(url)}"`);
      continue;
    }
    out.push(`${name}="${escapeAttr(value)}"`);
  }
  // An anchor may open a new tab; never hand it a live `window.opener`.
  if (tag === 'a' && hasHref) out.push('rel="noopener noreferrer"');
  return out.length > 0 ? ` ${out.join(' ')}` : '';
}

export function sanitizeRichText(html: string | null | undefined): string {
  if (!html) return '';

  return html
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(STRIP_WITH_CONTENT, '')
    .replace(TAG, (match, rawName: string, rawAttrs: string) => {
      const tag = rawName.toLowerCase();
      if (!ALLOWED_TAGS.has(tag)) return '';
      if (match.startsWith('</')) return VOID_TAGS.has(tag) ? '' : `</${tag}>`;
      return `<${tag}${sanitizeAttrs(tag, rawAttrs)}>`;
    });
}

// ---------------------------------------------------------------------------
// 3. richTextToPlainText — for surfaces that can only show text
// ---------------------------------------------------------------------------

const NAMED_ENTITIES: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
  hellip: '…',
  mdash: '—',
  ndash: '–',
  rsquo: '’',
  lsquo: '‘',
  rdquo: '”',
  ldquo: '“',
};

function decodeEntities(text: string): string {
  return text.replace(/&(#x?[0-9a-f]+|[a-z]+);?/gi, (match, body: string) => {
    if (body.startsWith('#')) {
      const isHex = body[1]?.toLowerCase() === 'x';
      const code = Number.parseInt(isHex ? body.slice(2) : body.slice(1), isHex ? 16 : 10);
      return Number.isFinite(code) && code > 0 && code <= 0x10ffff
        ? String.fromCodePoint(code)
        : match;
    }
    return NAMED_ENTITIES[body.toLowerCase()] ?? match;
  });
}

/**
 * Flatten a description for text-only surfaces (card previews, prefills).
 * Plain-text descriptions pass through untouched; block boundaries in HTML
 * become newlines so a `line-clamp` preview reads like prose instead of showing
 * raw `<p>` markup.
 */
export function richTextToPlainText(value: string | null | undefined): string {
  if (!value) return '';
  if (!isHtmlDescription(value)) return value;

  const text = value
    .replace(STRIP_WITH_CONTENT, '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|h[1-6]|li|blockquote|tr|pre)\s*>/gi, '\n')
    .replace(/<hr\s*\/?>/gi, '\n')
    .replace(/<[^>]*>/g, '');

  return decodeEntities(text)
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

// ---------------------------------------------------------------------------
// 4. descriptionToEditorHtml — seed the editor from either shape
// ---------------------------------------------------------------------------

function escapeText(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Convert a stored description into the HTML the editor should open with.
 *
 * Legacy plain text MUST go through this rather than straight into TipTap:
 * TipTap parses `content` as HTML, and HTML collapses newlines to spaces, so
 * feeding it raw plain text would silently flatten a host's paragraphs the
 * moment they opened the edit form. Blank-line-separated blocks become
 * paragraphs and single newlines become `<br>`, which is exactly what
 * `whitespace-pre-line` was rendering before.
 */
export function descriptionToEditorHtml(value: string | null | undefined): string {
  if (!value) return '';
  if (isHtmlDescription(value)) return value;

  return value
    .split(/\n{2,}/)
    .map((block) =>
      block
        .split('\n')
        .map((line) => escapeText(line.trim()))
        .filter((line) => line.length > 0)
        .join('<br>'),
    )
    .filter((block) => block.length > 0)
    .map((block) => `<p>${block}</p>`)
    .join('');
}
